/**
 * Add-on selections attached to a cart line.
 *
 * Two rules shape everything here:
 *
 * 1. A selection is part of the line's identity. "Large pizza + extra cheese"
 *    and "Large pizza" are different things to buy; merging them into one line
 *    because they share a product id would put an order in the system that
 *    does not match what the customer chose. `addonsKey` is what makes them
 *    distinct, in the cart and in the `cart_items` unique index alike.
 *
 * 2. The names and prices carried on a line are display only. Checkout
 *    re-resolves every add-on price from `addon_items` server-side — the same
 *    treatment the line's own price gets — so a tampered payload can change
 *    what the browser shows and nothing that is charged.
 */

export type CartAddon = {
  group_id: string;
  item_id: string;
  /** Snapshot for rendering. Never trusted for money. */
  name_ar: string;
  name_en: string;
  /** Snapshot for rendering. Never trusted for money. */
  price: number;
};

/**
 * Stable identity for a selection, order-independent so picking cheese then
 * olives keys the same as olives then cheese. Empty string when nothing is
 * selected, which is what every pre-existing line and every product without
 * add-ons carries — so the key of a line that has none is unchanged.
 */
export function addonsKey(
  addons: readonly Pick<CartAddon, 'item_id'>[] | null | undefined,
): string {
  if (!addons || addons.length === 0) return '';
  return [...new Set(addons.map((a) => a.item_id))].sort().join(',');
}

/** Per-unit add-on cost. Display only — see the note at the top. */
export function addonsUnitTotal(
  addons: readonly CartAddon[] | null | undefined,
): number {
  if (!addons) return 0;
  return addons.reduce((sum, a) => sum + (Number.isFinite(a.price) ? a.price : 0), 0);
}

/** The ids a client sends to the server; everything else is re-resolved. */
export function toAddonIds(
  addons: readonly CartAddon[] | null | undefined,
): { group_id: string; item_id: string }[] {
  if (!addons) return [];
  return addons.map((a) => ({ group_id: a.group_id, item_id: a.item_id }));
}
